import React, { Component } from "react";
import { AppCard, DataTableToolbar, PageLayout } from "../components/ui";

import _ from "lodash";
import { RequestLog } from "../models/Journaling";
import { dateBodyTemplate } from "../utils/columnTemplates";
import { journalingsApiService } from "../services/api/journalings/JournalingApiService";
import { SearchRequestBaseModel, SortOrderType } from "../models/SearchRequestModel";
import { toastService } from "../services/toastService";
import ExtendedDataTable from "../components/ExtendedDataTable";
import { Column, ColumnFilterElementTemplateOptions } from "primereact/column";
import { dateFilterTemplate, numberFilterMatchModeOptions, stringFilterMatchModeOptions } from "./Paginator";
import { merchantApiService } from "../services/api/merchants/MerchantApiService";
import { Dropdown } from "primereact/dropdown";
import { SelectItem } from "../models/SelectItem";
import { authUsersApiService } from "../services/api/clients/AuthUsersApiService";
import RequestLogDetailsDialog from "./Journaling/RequestLogDetails";
import { Activity } from "lucide-react";

interface RequestLogsState {
    requestLogs: RequestLog[];
    isLoading: boolean;
    totalRecords: number;
    viewRequestLogDetails: boolean;
    selectedRequestLog: RequestLog | null;
    merchants: SelectItem[];
    users: SelectItem[];
}

interface RequestLogComponentProps {
    filterFunction?: (searchRequest: SearchRequestBaseModel) => void;
}

export default class RequestLogComponent extends Component<RequestLogComponentProps, RequestLogsState> {
    private dataTable: ExtendedDataTable | null = null;

    constructor(props: RequestLogComponentProps | Readonly<RequestLogComponentProps>) {
        super(props);

        this.state = {
            requestLogs: [],
            isLoading: false,
            totalRecords: 0,
            viewRequestLogDetails: false,
            selectedRequestLog: null,
            merchants: [],
            users: [],
        };
    }

    loadJournalingData = async (searchRequest: SearchRequestBaseModel) => {
        this.setState({ isLoading: true });

        this.props.filterFunction?.(searchRequest);

        const [requestLogsResult, merchantResult, usersResult] = await Promise.all([
            journalingsApiService.getAllRequestLogs(searchRequest),
            merchantApiService.getSelectItem(),
            authUsersApiService.getSelectItem()
        ]);

        if (!requestLogsResult.success) {
            toastService.error(requestLogsResult.message);
            this.setState({
                isLoading
                    : false
            })

        }
        else {
            this.setState((prevState) => ({
                ...prevState,
                isLoading: false,
                merchants: merchantResult.success ? merchantResult.data : prevState.merchants,
                users: usersResult.success ? usersResult.data : prevState.users,
                requestLogs: requestLogsResult.success ? requestLogsResult.data : prevState.requestLogs,
                totalRecords: requestLogsResult.success ? requestLogsResult.totalCount : prevState.totalRecords
            }));
        }


    };


    loadRequestLogs = async (id: number) => {
        this.setState({ isLoading: true });
        const requestLogsResult = await journalingsApiService.getRequestLogsDetail(id);
        if (!requestLogsResult.success) {
            toastService.error(requestLogsResult.message);
            this.setState({ isLoading: false });
            return;
        }

        this.setState({ viewRequestLogDetails: true, selectedRequestLog: requestLogsResult.data });
        this.setState({ isLoading: false });
    };

    showJournalingDetails = (rowData: RequestLog) => {
        this.setState({ viewRequestLogDetails: true, selectedRequestLog: rowData });
    };

    hideJournalingDetailsView = () => {
        this.setState({ viewRequestLogDetails: false });
    };

    renderHeader = (hasActiveFilters: boolean) => <DataTableToolbar onClear={() => this.dataTable!.reset()} hasActiveFilters={hasActiveFilters} />;

    onRowClick = (event: { data: RequestLog }) => {
        this.loadRequestLogs(event.data.id);
    };

    merchantFilterTemplate = (options: ColumnFilterElementTemplateOptions) => {
        return (
            <Dropdown
                value={options.value}
                options={this.state.merchants}
                onChange={(e) => options.filterCallback(e.value)}
                optionLabel="label"
                optionValue="value"
                filter
                showClear
                placeholder="Select Merchants"
                className="p-column-filter"
            />
        );
    };

    usersFilterTemplate = (options: ColumnFilterElementTemplateOptions) => {
        return (
            <Dropdown
                value={options.value}
                options={this.state.users}
                onChange={(e) => options.filterCallback(e.value)}
                optionLabel="label"
                optionValue="value"
                filter
                showClear
                placeholder="Select Users"
                className="p-column-filter"
            />
        );
    };

    render() {
        return (
            <PageLayout heading="Access Logs" icon={Activity}>
                <AppCard className="mb-3">
                    <AppCard.Body className="!p-0">
                                        {this.state.viewRequestLogDetails && this.state.selectedRequestLog != null && (
                                            <RequestLogDetailsDialog
                                                isVisible={this.state.viewRequestLogDetails}
                                                onHide={this.hideJournalingDetailsView}
                                                requestLog={this.state.selectedRequestLog}
                                                editable={false}
                                                userEmail={this.state.users.find((a) => a.value == this.state.selectedRequestLog?.userId)?.label}
                                                merchantName={
                                                    this.state.merchants.find((a) => a.value == this.state.selectedRequestLog?.merchantId)?.label
                                                }
                                            />
                                        )}
                                        <ExtendedDataTable
                                            header={this.renderHeader}
                                            value={this.state.requestLogs}
                                            loading={this.state.isLoading}
                                            totalRecords={this.state.totalRecords}
                                            onChange={this.loadJournalingData}
                                            ref={(ref) => (this.dataTable = ref)}
                                            defaultSortOrder={SortOrderType.Descending}
                                            defaultSortField="madeOn"
                                            onRowClick={this.onRowClick}
                                        >
                                            <Column
                                                field="id"
                                                header="Id"
                                                {...({ alwaysVisible: true } as any)}
                                                filterField="id"
                                                filterMatchModeOptions={numberFilterMatchModeOptions}
                                            ></Column>
                                            <Column
                                                field="application"
                                                header="Application"
                                                {...({ alwaysVisible: true } as any)}
                                                filterField="application"
                                                filterMatchModeOptions={stringFilterMatchModeOptions}
                                            ></Column>
                                            <Column
                                                field="madeOn"
                                                filterField="madeOn"
                                                header="Event Date"
                                                {...({ alwaysVisible: true } as any)}
                                                body={dateBodyTemplate("madeOn")}
                                                dataType="date"
                                                filterElement={dateFilterTemplate}
                                                filterMatchModeOptions={numberFilterMatchModeOptions}
                                            ></Column>
                                            <Column
                                                field="controllerName"
                                                header="Controller Name"
                                                filterField="controllerName"
                                                filterMatchModeOptions={stringFilterMatchModeOptions}
                                            ></Column>
                                            <Column
                                                field="actionName"
                                                header="Action Name"
                                                filterField="actionName"
                                                filterMatchModeOptions={stringFilterMatchModeOptions}
                                            ></Column>
                                            <Column
                                                field="userId"
                                                header="User"
                                                filterMatchMode="equals"
                                                filterField="userId"
                                                filterMatchModeOptions={stringFilterMatchModeOptions}
                                                filterElement={this.usersFilterTemplate}
                                                body={(rowData: RequestLog) => (
                                                    <p>{this.state.users.find((a) => a.value == rowData.userId)?.label}</p>
                                                )}
                                            ></Column>
                                            <Column
                                                field="merchantId"
                                                header="Merchant"
                                                filterMatchMode="equals"
                                                filterField="merchantId"
                                                showFilterMatchModes={false}
                                                filterMatchModeOptions={stringFilterMatchModeOptions}
                                                filterElement={this.merchantFilterTemplate}
                                                body={(rowData: RequestLog) => (
                                                    <p>{this.state.merchants.find((a) => a.value == rowData.merchantId)?.label}</p>
                                                )}
                                            ></Column>
                                            <Column
                                                field="madeFromIPAddress"
                                                header="Ip Address"
                                                filterField="madeFromIPAddress"
                                                filterMatchModeOptions={stringFilterMatchModeOptions}
                                            ></Column>
                                        </ExtendedDataTable>
                    </AppCard.Body>
                </AppCard>
            </PageLayout>
        );
    }
}
